"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
} from "reactflow"
import Sidebar from "./Sidebar"
import { nodeTypes } from "./nodes"
import { toast } from "react-toastify"
import WorkflowEngine from "../utils/workflowEngine"
import NodeConfigPanel from "./NodeConfigPanel"
import WorkflowProgress from "./WorkflowProgress"
import NodeOutputPanel from "./NodeOutputPanel"
import ModernWorksheetViewer from "./ModernWorksheetViewer"

import "reactflow/dist/style.css"

const initialNodes = []
const initialEdges = []

let id = 0
const getId = () => `dndnode_${id++}`

const WorkflowCanvas = () => {
  const reactFlowWrapper = useRef(null)
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const [reactFlowInstance, setReactFlowInstance] = useState(null)
  const [workflowEngine] = useState(() => new WorkflowEngine())
  const [selectedNode, setSelectedNode] = useState(null)
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false)
  const [workflowResults, setWorkflowResults] = useState(new Map())

  const [isWorkflowRunning, setIsWorkflowRunning] = useState(false)
  const [workflowProgress, setWorkflowProgress] = useState(0)
  const [currentNode, setCurrentNode] = useState(null)
  const [nodeStatuses, setNodeStatuses] = useState({})
  const [completedNodes, setCompletedNodes] = useState(0)
  const [failedNodes, setFailedNodes] = useState(0)
  const [estimatedTime, setEstimatedTime] = useState(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [workflowStartTime, setWorkflowStartTime] = useState(null)

  const [selectedNodeForOutput, setSelectedNodeForOutput] = useState(null)
  const [isOutputPanelVisible, setIsOutputPanelVisible] = useState(false)

  const [selectedWorksheet, setSelectedWorksheet] = useState(null)
  const [isWorksheetViewerOpen, setIsWorksheetViewerOpen] = useState(false)

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Delete") {
        const selectedNodes = reactFlowInstance.getNodes().filter((node) => node.selected)
        const selectedEdges = reactFlowInstance.getEdges().filter((edge) => edge.selected)

        if (selectedNodes.length > 0 || selectedEdges.length > 0) {
          setNodes((nds) => nds.filter((node) => !node.selected))
          setEdges((eds) => eds.filter((edge) => !edge.selected))
          if (selectedNodes.length > 0) toast.info(`Deleted ${selectedNodes.length} node(s)`)
          if (selectedEdges.length > 0) toast.info(`Deleted ${selectedEdges.length} edge(s)`)
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [reactFlowInstance, setNodes, setEdges])

  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }, [])

  const onDrop = useCallback(
    (event) => {
      event.preventDefault()

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect()
      const type = event.dataTransfer.getData("application/reactflow")

      if (typeof type === "undefined" || !type) {
        return
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      })

      const newNode = {
        id: getId(),
        type,
        position,
        data: {
          label: `${type} node`,
          config: getDefaultConfig(type),
        },
      }

      setNodes((nds) => nds.concat(newNode))
      toast.success(`Added ${type} block to workflow`)
    },
    [reactFlowInstance, setNodes],
  )

  const getDefaultConfig = (type) => {
    const configs = {
      prompt: { prompt: "", variables: [] },
      textGen: { model: "gemini", gradeLevel: "4", language: "English", concept: "" },
      visual: { concept: "", style: "educational" },
      worksheet: { concept: "", grades: ["3", "4", "5"], language: "English" },
      audio: { text: "", language: "English", voice: "default" },
      upload: { fileType: "image", maxSize: "10MB" },
      input: { inputType: "text", placeholder: "Enter text..." },
      api: { apiType: "wikipedia", query: "education", url: "", method: "GET", headers: {}, body: {}, language: "en" },
      offlineLLM: { model: "vidya-agent", prompt: "", language: "English", gradeLevel: "4", apiUrl: process.env.REACT_APP_VIDYA_AGENT_URL, endpoint: "api/generate-content" },
    }
    return configs[type] || {}
  }

  const onNodeClick = useCallback(
    (event, node) => {
      if (isWorkflowRunning || workflowResults.has(node.id)) {
        const result = workflowResults.get(node.id)
        if (result) {
          setSelectedNodeForOutput(node)
          setIsOutputPanelVisible(true)
          return
        }
      }

      setSelectedNode(node)
      setIsConfigPanelOpen(true)
    },
    [isWorkflowRunning, workflowResults],
  )

  const handleNodeConfigSave = useCallback(
    (nodeId, newConfig) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, config: newConfig, label: getNodeLabel(node.type, newConfig) } }
            : node,
        ),
      )
    },
    [setNodes],
  )

  const getNodeLabel = (type, config) => {
    switch (type) {
      case "prompt":
        return config.prompt ? `Prompt: ${config.prompt.substring(0, 30)}...` : "Prompt Block"
      case "textGen":
        return config.concept ? `Text Gen: ${config.concept}` : "Gemini Text Gen"
      case "visual":
        return config.concept ? `Visual: ${config.concept}` : "Visual Generator"
      case "worksheet":
        return config.concept ? `Worksheet: ${config.concept}` : "Worksheet Creator"
      case "audio":
        return config.text ? `Audio: ${config.text.substring(0, 20)}...` : "Text to Speech"
      case "api":
        return config.apiType ? `API: ${config.apiType}` : "External API"
      default:
        return `${type} node`
    }
  }

  const updateNodeStatus = useCallback(
    (nodeId, status, error = null) => {
      setNodeStatuses((prev) => ({
        ...prev,
        [nodeId]: { state: status, label: nodes.find((n) => n.id === nodeId)?.data?.label || nodeId, error: error },
      }))

      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return { ...node, className: `status-${status} ${workflowResults.has(nodeId) ? "has-output" : ""}`.trim() }
          }
          return node
        }),
      )
    },
    [nodes, setNodes, workflowResults],
  )

  const runWorkflow = useCallback(async () => {
    if (nodes.length === 0) {
      toast.warn("Add some blocks to your workflow first!")
      return
    }

    setIsWorkflowRunning(true)
    setWorkflowProgress(0)
    setCompletedNodes(0)
    setFailedNodes(0)
    setWorkflowStartTime(Date.now())
    setElapsedTime(0)
    setWorkflowResults(new Map())
    setNodeStatuses({})

    const estimatedSeconds = workflowEngine.calculateEstimatedTime(nodes, edges)
    const validSeconds = Number(estimatedSeconds) || 0
    const formattedTime = `${Math.floor(validSeconds / 60)}:${(validSeconds % 60).toString().padStart(2, "0")}`
    setEstimatedTime(formattedTime)

    nodes.forEach((node) => updateNodeStatus(node.id, "pending"))

    try {
      const executionGraph = workflowEngine.buildExecutionGraph(nodes, edges)
      const results = await workflowEngine.executeNodesWithProgress(executionGraph, nodes.length, {
        onNodeStart: (nodeId) => {
          setCurrentNode(nodes.find((n) => n.id === nodeId)?.data?.label || nodeId)
          updateNodeStatus(nodeId, "running")
        },
        onNodeComplete: (nodeId, result) => {
          updateNodeStatus(nodeId, "completed")
          setWorkflowResults((prev) => new Map(prev).set(nodeId, result))
          setCompletedNodes((prev) => prev + 1)
          setWorkflowProgress((prev) => Math.min(100, ((completedNodes + 1) / nodes.length) * 100))
          toast.success(`✅ ${nodeId}: Completed successfully!`, { autoClose: 2000 })
        },
        onNodeError: (nodeId, error) => {
          updateNodeStatus(nodeId, "failed", error.message)
          setFailedNodes((prev) => prev + 1)
          setWorkflowProgress((prev) => Math.min(100, ((completedNodes + failedNodes + 1) / nodes.length) * 100))
          toast.error(`❌ ${nodeId}: ${error.message}`, { autoClose: 5000 })
        },
        onProgressUpdate: (progress) => {
          setWorkflowProgress(progress)
          setElapsedTime(Math.floor((Date.now() - workflowStartTime) / 1000))
        },
      })

      setWorkflowResults(results)
      setCurrentNode(null)

      const successCount = Array.from(results.values()).filter((r) => r.output).length
      const failureCount = Array.from(results.values()).filter((r) => r.error).length

      if (failureCount === 0) {
        toast.success(`🎉 Workflow completed! All ${successCount} nodes executed successfully.`, { autoClose: 8000 })
      } else {
        toast.warn(`⚠️ Workflow completed with issues: ${successCount} successful, ${failureCount} failed.`, {
          autoClose: 8000,
        })
      }
    } catch (error) {
      console.error("Workflow execution failed:", error)
      toast.error(`Workflow execution failed: ${error.message}`)
      setFailedNodes(nodes.length)
    } finally {
      setIsWorkflowRunning(false)
      setWorkflowProgress(100)
      setElapsedTime(Math.floor((Date.now() - (workflowStartTime || Date.now())) / 1000))
    }
  }, [nodes, edges, workflowEngine, updateNodeStatus, workflowStartTime, completedNodes, failedNodes])

  const saveWorkflow = useCallback(() => {
    const workflow = {
      nodes,
      edges,
      name: `Workflow_${new Date().toISOString().split("T")[0]}`,
      created: new Date().toISOString(),
    }
    const savedWorkflows = JSON.parse(localStorage.getItem("sahayakFlows") || "[]")
    savedWorkflows.push(workflow)
    localStorage.setItem("sahayakFlows", JSON.stringify(savedWorkflows))
    toast.success("Workflow saved successfully!")
  }, [nodes, edges])

  const loadSampleWorkflow = useCallback(
    (workflow) => {
      setNodes(workflow.nodes)
      setEdges(workflow.edges)
      toast.success(`Loaded "${workflow.name}" workflow template!`)
    },
    [setNodes, setEdges],
  )

  useEffect(() => {
    window.sahayakFlow = { runWorkflow, saveWorkflow }
  }, [runWorkflow, saveWorkflow])

  const renderWorkflowResults = () => {
    if (workflowResults.size === 0) return null

    return (
      <div className="bg-white border-t border-gray-200 p-4 max-h-64 overflow-y-auto">
        <h3 className="text-lg font-semibold mb-3 text-gray-800">Workflow Results</h3>
        <div className="space-y-3">
          {Array.from(workflowResults.entries()).map(([nodeId, result]) => (
            <div key={nodeId} className="border border-gray-200 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-gray-700 capitalize">{String(result.type)} Result</span>
                <span className="text-xs text-gray-500">{new Date(result.timestamp).toLocaleTimeString()}</span>
              </div>
              {/* ... result rendering logic remains the same ... */}
            </div>
          ))}
        </div>
        <button
          onClick={() => setWorkflowResults(new Map())}
          className="mt-3 px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
        >
          Clear Results
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full">
      <Sidebar onLoadSampleWorkflow={loadSampleWorkflow} />
      <div className="flex-grow flex flex-col relative">
        <div className="absolute top-0 left-0 right-0 z-10 w-full">
          <WorkflowProgress
            isRunning={isWorkflowRunning}
            progress={workflowProgress}
            currentNode={currentNode}
            totalNodes={nodes.length}
            completedNodes={completedNodes}
            failedNodes={failedNodes}
            estimatedTime={estimatedTime}
            elapsedTime={elapsedTime}
            nodeStatuses={nodeStatuses}
            onPauseResume={() => toast.info("Pause/Resume functionality coming soon!")}
            onStop={() => {
              setIsWorkflowRunning(false)
              setCurrentNode(null)
              toast.warn("Workflow stopped by user")
            }}
          />
        </div>
        <div className="flex-grow" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
          >
            <Controls />
            <MiniMap />
            <Background color="#000000" gap={20} variant="dots" />
          </ReactFlow>
        </div>

        {!isWorkflowRunning && workflowResults.size > 0 && (
          <div className="w-full z-10 border-t">{renderWorkflowResults()}</div>
        )}
      </div>

      <NodeConfigPanel
        node={selectedNode}
        isOpen={isConfigPanelOpen}
        onClose={() => setIsConfigPanelOpen(false)}
        onSave={handleNodeConfigSave}
      />

      <NodeOutputPanel
        node={selectedNodeForOutput}
        result={selectedNodeForOutput ? workflowResults.get(selectedNodeForOutput.id) : null}
        isVisible={isOutputPanelVisible}
        onClose={() => {
          setIsOutputPanelVisible(false)
          setSelectedNodeForOutput(null)
        }}
      />

      {isWorksheetViewerOpen && selectedWorksheet && (
        <ModernWorksheetViewer
          worksheet={selectedWorksheet}
          onClose={() => {
            setIsWorksheetViewerOpen(false)
            setSelectedWorksheet(null)
          }}
        />
      )}
    </div>
  )
}

export default () => (
  <ReactFlowProvider>
    <WorkflowCanvas />
  </ReactFlowProvider>
)
